/*
 * Copyright 2026 Sony Corporation
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import type { AgentOp, RequestOps } from '../services/agent';

/**
 * One chat thread, and what it did.
 *
 * The agent is not a second write path (devplan §3.2): everything here ends in
 * the same `apply` the GUI uses — the document reducer on the Scene page, the
 * existing REST and shadow calls on the Device page — so a turn cannot reach
 * anything by a route a user could not.
 */

/**
 * Above this many ops a turn is proposed rather than applied (devplan §7.4
 * rule 5). Small edits are undoable and free, so asking about them is ceremony;
 * rearranging half a scene is not something to discover after the fact.
 */
export const CONFIRM_ABOVE = 20;

export interface ChatMessage<Op extends AgentOp = AgentOp> {
  id: string;
  role: 'user' | 'assistant';
  text: string;
  /** Absent on a turn that proposed no change. */
  ops?: readonly Op[];
  status?: 'applied' | 'pending' | 'discarded';
  /** What applying them refused to do, when the apply path can say. */
  errors?: readonly string[];
}

/** Void where applying cannot fail locally; otherwise what it could not do. */
export type ApplyOps<Op extends AgentOp> = (ops: readonly Op[]) => void | Promise<readonly string[]>;

export interface UseAgentOptions<Op extends AgentOp, Digest> {
  /** Read when a turn resolves, so what it is applied to is what is on screen then. */
  digest: () => Digest;
  apply: ApplyOps<Op>;
  requestOps: RequestOps<Op, Digest>;
  /** Beyond the batch-size rule: the Device page also confirms anything twin-creating. */
  needsConfirmation?: (ops: readonly Op[]) => boolean;
}

export interface UseAgentResult<Op extends AgentOp> {
  messages: readonly ChatMessage<Op>[];
  /** Ops waiting for a click, so the page can show what would happen. */
  pending: readonly Op[];
  /** A turn is in flight. The input stays live; sending during one is not. */
  busy: boolean;
  error?: string;
  send: (prompt: string) => void;
  apply: (messageId: string) => void;
  discard: (messageId: string) => void;
  newThread: () => void;
}

const messageId = (): string => `msg_${Math.random().toString(36).slice(2, 10)}`;

export const useAgent = <Op extends AgentOp, Digest>({
  digest,
  apply,
  requestOps,
  needsConfirmation,
}: UseAgentOptions<Op, Digest>): UseAgentResult<Op> => {
  const [messages, setMessages] = useState<readonly ChatMessage<Op>[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();

  // Read when a turn resolves, not when it was sent: what it has to be applied
  // to is what is on screen now.
  const latest = useRef({ digest, apply, requestOps, needsConfirmation });
  useEffect(() => {
    latest.current = { digest, apply, requestOps, needsConfirmation };
  }, [digest, apply, requestOps, needsConfirmation]);

  // A reply that lands after *New thread* belongs to a conversation that is gone.
  const thread = useRef(0);

  /** Applying is asynchronous on the Device page, so what it refused arrives late. */
  const run = useCallback((id: string, ops: readonly Op[]) => {
    const outcome = latest.current.apply(ops);
    if (!outcome) {
      return;
    }
    void outcome.then((errors) => {
      if (errors.length > 0) {
        setMessages((entries) => entries.map((entry) => (entry.id === id ? { ...entry, errors } : entry)));
      }
    });
  }, []);

  const send = useCallback(
    (prompt: string) => {
      const text = prompt.trim();
      if (text === '') {
        return;
      }
      const turn = thread.current;

      setMessages((entries) => [...entries, { id: messageId(), role: 'user', text }]);
      setBusy(true);
      setError(undefined);

      void (async () => {
        try {
          const reply = await latest.current.requestOps(text, latest.current.digest());
          if (turn !== thread.current) {
            return;
          }

          const confirm = reply.ops.length > CONFIRM_ABOVE || (latest.current.needsConfirmation?.(reply.ops) ?? false);
          const id = messageId();
          setMessages((entries) => [
            ...entries,
            {
              id,
              role: 'assistant',
              text: reply.text,
              ...(reply.ops.length > 0
                ? { ops: reply.ops, status: confirm ? ('pending' as const) : ('applied' as const) }
                : {}),
            },
          ]);
          if (reply.ops.length > 0 && !confirm) {
            run(id, reply.ops);
          }
        } catch (cause) {
          if (turn === thread.current) {
            setError(cause instanceof Error ? cause.message : 'The assistant could not answer.');
          }
        } finally {
          if (turn === thread.current) {
            setBusy(false);
          }
        }
      })();
    },
    [run]
  );

  // Not inside the state updater: React may run one twice, and a batch applied
  // twice is twenty-four devices on top of another twenty-four.
  const current = useRef(messages);
  useEffect(() => {
    current.current = messages;
  }, [messages]);

  const applyMessage = useCallback(
    (id: string) => {
      const message = current.current.find((entry) => entry.id === id);
      if (!message?.ops || message.status !== 'pending') {
        return;
      }
      setMessages((entries) =>
        entries.map((entry) => (entry.id === id ? { ...entry, status: 'applied' as const } : entry))
      );
      run(id, message.ops);
    },
    [run]
  );

  const discard = useCallback((id: string) => {
    setMessages((entries) =>
      entries.map((entry) =>
        entry.id === id && entry.status === 'pending' ? { ...entry, status: 'discarded' } : entry
      )
    );
  }, []);

  const newThread = useCallback(() => {
    thread.current += 1;
    setMessages([]);
    setBusy(false);
    setError(undefined);
  }, []);

  // Memoised: the viewport previews these, and a new array every render would
  // have the page re-rendering itself for as long as the panel is open.
  const pending = useMemo(
    () => messages.filter((message) => message.status === 'pending').flatMap((message) => message.ops ?? []),
    [messages]
  );

  return { messages, pending, busy, error, send, apply: applyMessage, discard, newThread };
};
